import { useCallback, useEffect, useRef, useState } from "react";
import {
  Link,
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  LayoutDashboard,
  Gift,
  Settings,
  Loader2,
  Building2,
  AlertCircle,
  Shield,
  HelpCircle,
  History,
  Monitor,
} from "lucide-react";
import { useAdminUser } from "../../hooks/useAdminUser";
import { appListSucursales, type SucursalPublic } from "../../lib/api";
import { SUPER_ONLY_PATHS } from "../../lib/tours";
import {
  getSelectedSucursal,
  setSelectedSucursal,
} from "../../lib/admin-storage";
import { isAppMode } from "../../lib/app-storage";
import { UserMenu } from "../../components/admin/UserMenu";
import { AdminHelp } from "../../components/admin/AdminHelp";
import { useDialogs } from "../../hooks/useDialogs";

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dialogs = useDialogs();
  const { user, loading, error, devEmail, setDevEmail, isDev, clearDev } =
    useAdminUser();
  // Registro de "cambios sin guardar" de la pagina hija activa. La pagina
  // (Mi pantalla / Ajustes del shopping) llama setPageDirty(true/false);
  // al cambiar de sucursal avisamos antes de descartar (el cambio de
  // sucursal remonta el contenido y perderia los cambios).
  const dirtyRef = useRef(false);
  const setPageDirty = useCallback((v: boolean) => {
    dirtyRef.current = v;
  }, []);
  const [emailInput, setEmailInput] = useState(devEmail ?? "");
  const [sucursales, setSucursales] = useState<SucursalPublic[]>([]);
  const [selectedSucursal, setSelectedSucursalState] = useState<number | null>(
    () => getSelectedSucursal(),
  );

  // Si la PC está en modo pantalla activo, NO mostrar admin sin desbloqueo.
  // (Por ahora redirect a / con mensaje; en futuro: modal pidiendo PIN).
  useEffect(() => {
    if (isAppMode()) {
      // TODO: en lugar de redirect simple, modal PIN para "salir del modo pantalla"
      // Por ahora dejo entrar pero podríamos bloquear.
      console.warn(
        "Esta PC está en modo pantalla. En prod requeriría PIN para entrar a /admin.",
      );
    }
  }, []);

  // Cargar sucursales para selector super_admin
  useEffect(() => {
    if (user?.role !== "super_admin") return;
    appListSucursales()
      .then((rows) => {
        setSucursales(rows);
        if (!selectedSucursal && rows.length > 0) {
          setSelectedSucursalState(rows[0].id);
          setSelectedSucursal(rows[0].id);
        }
      })
      .catch(() => {});
  }, [user, selectedSucursal]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin opacity-50" />
      </main>
    );
  }

  // En dev: si no hay email seteado o el backend rechaza, mostrar form
  // para configurarlo.
  if (isDev && (!devEmail || error)) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl bg-[#1a1540] p-8 shadow-2xl">
          <h1 className="mb-2 text-2xl font-bold">Dev: identificarte</h1>
          <p className="mb-6 text-sm opacity-70">
            En prod esto lo hace Cloudflare Access. En dev ingresá el email
            que querés impersonar (debe estar en <code>user_roles</code>).
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setDevEmail(emailInput.trim().toLowerCase());
            }}
            className="flex flex-col gap-4"
          >
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="marketingscsdg@gmail.com"
              className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
              required
            />
            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error.message}</span>
              </div>
            )}
            <button
              type="submit"
              className="rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold"
            >
              Continuar
            </button>
          </form>
        </div>
      </main>
    );
  }

  if (error || !user || !user.role) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-400" />
        <p className="text-red-400">
          {error?.message ?? "Tu email no tiene rol asignado."}
        </p>
        <button
          onClick={() => navigate("/")}
          className="rounded-lg bg-white/10 px-4 py-2 hover:bg-white/20"
        >
          Volver
        </button>
      </main>
    );
  }

  const isSuper = user.role === "super_admin";
  // Guard de routing super-only en render-time (no useEffect): si un
  // admin común escribe manualmente /admin/sucursales etc., devolvemos
  // <Navigate> ANTES de pintar el layout. Con el useEffect anterior
  // había 1-2 frames donde la página hija arrancaba a montar (incluso
  // disparando su fetch) hasta que el effect corría. El backend ya
  // rechaza el fetch con 403, pero ahorrar el roundtrip y el flash de
  // UI sensible vale.
  if (!isSuper && SUPER_ONLY_PATHS.has(location.pathname)) {
    return <Navigate to="/admin" replace />;
  }

  const sucursalIdEfectiva = isSuper
    ? selectedSucursal ?? undefined
    : user.sucursal_id ?? undefined;

  return (
    // Desktop (md+): layout de alto fijo igual al viewport, sin scroll en
    // el <body>. Cada hijo (aside y main) maneja su propio overflow → el
    // sidebar queda fijo mientras solo el contenido de la derecha
    // scrollea. Mobile: comportamiento clásico apilado, scroll de página
    // entera (un sidebar fijo arriba en mobile sería molesto).
    // min-h-0 en los hijos flex es obligatorio: sin eso, el flex item
    // toma min-height: auto y el overflow-y-auto no dispara nunca.
    <div className="flex min-h-screen flex-col md:h-screen md:flex-row md:overflow-hidden">
      {/* Sidebar */}
      <aside className="w-full shrink-0 border-b border-white/10 bg-[#100d2e] p-4 md:w-64 md:border-b-0 md:border-r md:h-full md:overflow-y-auto">
        <div className="mb-6">
          <Link to="/admin" className="text-xl font-bold">
            JuegosSC <span className="opacity-50">admin</span>
          </Link>
          {/* UserMenu reemplaza el bloque viejo de "email + badge rol".
              Trigger compacto con avatar, dropdown con Perfil + Logout. */}
          <div className="mt-3" data-tour="user-menu">
            <UserMenu
              user={user}
              onLogoutDev={() => {
                clearDev();
                // Recarga la página para volver al estado de "Dev:
                // identificarte" en el AdminLayout.
                window.location.reload();
              }}
            />
          </div>
        </div>

        {/* Selector de sucursal activa (super_admin). Vive en el sidebar (no
            en un banner arriba del contenido): es contexto fijo y no merece
            comerse el alto del contenido. Cambiarlo remonta el contenido por
            el key del wrapper de abajo (cada pagina re-fetchea). */}
        {isSuper && (
          <div className="mb-4" data-tour="sidebar-sucursal">
            <label
              htmlFor="sucursal-activa-sel"
              className="mb-1 flex items-center gap-1.5 px-1 text-xs uppercase tracking-wider opacity-50"
            >
              <Building2 className="h-3 w-3 text-[#a78bfa]" />
              Sucursal activa
            </label>
            <select
              id="sucursal-activa-sel"
              value={selectedSucursal ?? ""}
              onChange={async (e) => {
                const id = parseInt(e.target.value, 10);
                if (id === selectedSucursal) return;
                // Aviso si la pagina actual tiene cambios sin guardar (cambiar
                // de sucursal remonta el contenido y los pierde).
                if (dirtyRef.current) {
                  const ok = await dialogs.confirm({
                    title: "Cambios sin guardar",
                    message:
                      "Tenés cambios sin guardar en esta página. Si cambiás de sucursal se van a perder. ¿Continuar?",
                    confirmLabel: "Cambiar y descartar",
                    variant: "destructive",
                  });
                  if (!ok) return;
                }
                dirtyRef.current = false;
                setSelectedSucursalState(id);
                setSelectedSucursal(id);
              }}
              className="w-full rounded bg-white/10 px-2.5 py-1.5 text-sm font-bold outline-none focus:ring-2 focus:ring-[#a78bfa]"
            >
              {sucursales.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </div>
        )}

        <nav className="flex flex-col gap-1" data-tour="sidebar-nav">
          <SidebarLink to="/admin" end icon={<LayoutDashboard className="h-4 w-4" />}>
            Inicio
          </SidebarLink>
          <SidebarLink to="/admin/productos" icon={<Gift className="h-4 w-4" />}>
            Premios
          </SidebarLink>
          <SidebarLink to="/admin/ganadores" icon={<History className="h-4 w-4" />}>
            Jugadas
          </SidebarLink>
          <SidebarLink to="/admin/pantalla" icon={<Monitor className="h-4 w-4" />}>
            Mi pantalla
          </SidebarLink>
        </nav>

        {isSuper && (
          <div className="mt-6">
            <div className="mb-2 flex items-center gap-2 px-3 text-xs uppercase tracking-wider opacity-50">
              <Shield className="h-3 w-3" />
              Encargado del sistema
            </div>
            <nav className="flex flex-col gap-1">
              <SidebarLink to="/admin/shopping" icon={<Settings className="h-4 w-4" />}>
                Ajustes del shopping
              </SidebarLink>
              {/* Campañas se movió a una pestaña dentro de Administración
                  (era 1 solo propósito, no compensaba pestaña aparte). */}
              <SidebarLink to="/admin/organizacion" icon={<Building2 className="h-4 w-4" />}>
                Administración
              </SidebarLink>
              <SidebarLink to="/admin/faqs" icon={<HelpCircle className="h-4 w-4" />}>
                Editar ayuda
              </SidebarLink>
            </nav>
          </div>
        )}

        <div className="mt-6 border-t border-white/10 pt-4">
          <Link
            to="/"
            className="block rounded px-3 py-2 text-sm opacity-60 hover:bg-white/5 hover:opacity-100"
          >
            ← Volver a la pantalla
          </Link>
        </div>
      </aside>

      {/* Main — min-h-0 es crítico: sin eso el flex item toma
          min-height:auto y el overflow-y-auto del main no scrollea (la
          página entera empieza a scrollear y el sidebar deja de quedar
          fijo). Truco clásico de flex+overflow. */}
      <main className="flex-1 p-6 md:h-full md:min-h-0 md:overflow-y-auto">
        {/* El selector de sucursal activa vive ahora en el sidebar (no acá
            arriba). key = sucursal activa: al cambiarla (super_admin) se
            remonta el contenido y cada página vuelve a pedir sus datos. Para
            el admin común el id es fijo, así que nunca remonta. */}
        <div key={sucursalIdEfectiva ?? "sin-sucursal"}>
          <Outlet context={{ user, sucursalIdEfectiva, setPageDirty }} />
        </div>
      </main>

      {/* Ayuda flotante (botón "?" + tour driver.js + modal FAQ). Solo
          se monta DENTRO del admin, así nunca aparece en la pantalla
          pública. Auto-tour la primera vez por user (flag localStorage).
          Le pasamos el rol para que el tour y la FAQ se customicen —
          admins comunes no ven steps/items de páginas super_admin. */}
      <AdminHelp role={user.role} />
    </div>
  );
}

function SidebarLink({
  to,
  end,
  icon,
  children,
}: {
  to: string;
  end?: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-2 rounded px-3 py-2 text-sm transition ${
          isActive
            ? "bg-[#a78bfa]/20 text-[#c4b5fd]"
            : "opacity-70 hover:bg-white/5 hover:opacity-100"
        }`
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}

// Hook helper para que las páginas hijas accedan al context del layout.
import { useOutletContext } from "react-router-dom";
import type { AdminUser } from "../../hooks/useAdminUser";

export type AdminCtx = {
  user: AdminUser;
  sucursalIdEfectiva: number | undefined;
  /** La pagina hija reporta si tiene cambios sin guardar, para avisar
   *  antes de cambiar de sucursal (que la remonta). Opcional: las
   *  paginas que no son formularios simplemente no lo llaman. */
  setPageDirty: (v: boolean) => void;
};

export function useAdminCtx(): AdminCtx {
  return useOutletContext<AdminCtx>();
}
